import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Alert,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { ACLAuthorization } from '@/api/bol';
import { useMembers } from '@/hooks/useMembers';

interface ACLAuthorizationDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (authorizedBy: string) => Promise<void>;
  authorization: ACLAuthorization | null;
  controlMemberIdentifiers: string[];
}

const ACLAuthorizationDeleteDialog: React.FC<ACLAuthorizationDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  authorization,
  controlMemberIdentifiers
}) => {
  const { members, membersLoading, filteredMembers, fetchMembers } = useMembers();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorizedBy, setAuthorizedBy] = useState('');

  const authorizedByMembers = useMemo(
    () => filteredMembers
      .filter((member) => !member.disabled)
      .filter((member) => controlMemberIdentifiers.includes(member.identifier)),
    [filteredMembers, controlMemberIdentifiers]
  );

  useEffect(() => {
    if (open && members.length === 0) {
      fetchMembers();
    }
  }, [open, members.length, fetchMembers]);

  useEffect(() => {
    if (open) {
      setAuthorizedBy(authorizedByMembers[0]?.identifier || '');
      setError(null);
    }
  }, [open, authorizedByMembers]);

  const handleConfirm = async () => {
    setError(null);

    if (!authorizedBy) {
      setError('Please select a member identifier to authorize this deletion');
      return;
    }

    setDeleting(true);
    try {
      await onConfirm(authorizedBy);
      handleClose();
    } catch (err) {
      console.error('Failed to delete ACL authorization:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete authorization');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      setAuthorizedBy('');
      setError(null);
      onClose();
    }
  };

  if (!authorization) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={deleting}
    >
      <DialogTitle>Delete ACL Authorization</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <DialogContentText>
          Are you sure you want to delete this ACL authorization?
        </DialogContentText>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Member:</strong> {authorization.memberIdentifier}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Access Mode:</strong> {authorization.mode}
          </Typography>
          <Typography variant="body2">
            <strong>Authorized By:</strong> {authorization.authorizedBy}
          </Typography>
        </Box>

        <FormControl fullWidth required sx={{ mt: 2 }}>
          <InputLabel id="authorized-by-delete-label">Member Identifier</InputLabel>
          <Select
            labelId="authorized-by-delete-label"
            value={authorizedBy}
            onChange={(e: SelectChangeEvent<string>) => setAuthorizedBy(e.target.value)}
            label="Member Identifier"
            disabled={deleting || membersLoading}
            endAdornment={
              <IconButton onClick={fetchMembers} disabled={membersLoading || deleting} sx={{ mr: 3 }}>
                <Tooltip title="Refresh members">
                  <Refresh />
                </Tooltip>
              </IconButton>
            }
          >
            {membersLoading && <MenuItem disabled>Loading members...</MenuItem>}
            {!membersLoading && authorizedByMembers.length === 0 && (
              <MenuItem disabled>No members available</MenuItem>
            )}
            {!membersLoading && authorizedByMembers.length > 0 && authorizedByMembers.map((member) => (
              <MenuItem 
                key={member.identifier} 
                value={member.identifier}
              >
                {member.identifier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="warning" sx={{ mt: 2 }}>
          This action cannot be undone. The member will lose access to this logistics object.
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={deleting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="error" 
          variant="contained"
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ACLAuthorizationDeleteDialog;
